import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { format } from 'date-fns';
import { IFeedbackSerialized, Reviews } from '@bridebook/web/app-shared/lib/supplier/types';
import { IPhoto } from '@bridebook/models/source/models/Suppliers/Photos.types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { translateImgixToCloudflare } from '@bridebook/toolbox/src/buildImageUrl/translate-imgix-to-cloudflare';

/**
 * See required fields of the schema:
 * https://schema.org/LocalBusiness
 * https://schema.org/AggregateRating
 *
 * Schema validator: https://developers.google.com/search/docs/advanced/structured-data
 * Jira: https://bridebook.atlassian.net/browse/LIVE-13694
 */
interface IMicroData {
  '@context': 'https://schema.org';
  '@type': 'LocalBusiness';
  '@id': string;
  logo?: string;
  url: string;
  name: string;
  description: string;
  address: {
    '@type': 'PostalAddress';
    streetAddress: string;
    postalCode?: string;
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  };
  geo?: {
    '@type': 'GeoCoordinates';
    latitude: number;
    longitude: number;
  };
  image?: {
    '@type': 'ImageObject';
    url: string;
  }[];
  aggregateRating?: {
    '@type': 'AggregateRating';
    reviewCount: number;
    ratingValue: number;
    itemReviewed: {
      '@type': 'LocalBusiness';
      '@id': string;
      name: string;
    };
  };
  review?: {
    '@type': 'Review';
    datePublished: string;
    description: string;
    author: {
      '@type': 'Person';
      name: string;
    };
    reviewRating: {
      '@type': 'Rating';
      ratingValue: number;
      bestRating: 5;
      worstRating: 1;
    };
  }[];
}

const getAddress = (supplier: ISupplier): IMicroData['address'] => {
  const addressLocality = supplier?.address?.city;
  const addressRegion = supplier?.address?.adminArea?.[0];
  const postalCode = supplier?.address?.postalCode;
  const streetAddress = supplier?.address?.street?.[0];
  const addressCountry = supplier?.address?.country;

  return {
    '@type': 'PostalAddress',
    addressLocality,
    addressRegion,
    postalCode,
    streetAddress,
    addressCountry,
  };
};

const getAggregatedRating = (
  ratingValue: number,
  reviewCount: number,
  itemReviewedName: string,
  canonicalUrl: string,
): IMicroData['aggregateRating'] => ({
  '@type': 'AggregateRating',
  ratingValue,
  reviewCount,
  itemReviewed: {
    '@type': 'LocalBusiness',
    '@id': canonicalUrl,
    name: itemReviewedName,
  },
});

export const generateSupplierMicrodata = (
  supplier: ISupplier,
  canonicalUrl: string,
  metaDesc: string,
  reviews: Reviews,
  reviewsCount: number,
  photos?: IPhoto[],
): IMicroData => {
  const { name, coordinates } = supplier;
  const address = getAddress(supplier);
  const images = photos?.map((photo) => `${IMAGES_BASE_URL}/${photo.path}`) || [];

  const data: IMicroData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': canonicalUrl,
    name,
    logo: images[0],
    url: canonicalUrl,
    description: metaDesc,
    address,
    image: images.map((src) => ({
      '@type': 'ImageObject',
      url: translateImgixToCloudflare({ src, w: 460, h: 310 }),
    })),
  };

  if (reviewsCount > 0 && reviews?.stars > 0) {
    data.aggregateRating = getAggregatedRating(reviews.stars, reviewsCount, name, canonicalUrl);
  }

  if (coordinates) {
    const { _latitude: latitude, _longitude: longitude } = coordinates as unknown as {
      _latitude: number;
      _longitude: number;
    };

    if (latitude && longitude) {
      data.geo = {
        '@type': 'GeoCoordinates',
        latitude,
        longitude,
      };
    }
  }

  if (reviews.feedback) {
    const feedback = reviews.feedback.filter(
      (f) => !!f.createdAt && !!f.body && !!f.name && !!f.stars,
    ) as IFeedbackSerialized[];

    data.review = feedback.map((feedback) => ({
      '@type': 'Review',
      datePublished: format(new Date(feedback.createdAt), 'yyyy-MM-dd'),
      description: feedback.body,
      author: {
        '@type': 'Person',
        name: feedback.name,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: feedback.stars || 5,
        bestRating: 5,
        worstRating: 1,
      },
    }));
  }

  return data;
};
